import BadgeWithRemove from 'components/BadgeWithRemove/BadgeWithRemove';
import NoRecordFound from 'components/NoRecordFound/NoRecordFound';
import { useAtomValue, useSetAtom } from 'jotai';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import PerfectScrollbar from 'perfect-scrollbar';
import React, { CSSProperties, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
} from 'reactstrap';
import { companyIdAtom } from 'views/state';

import { unsubscribe } from './helper';
import {
  productSubscriptionsAtom,
  refreshSubscriptionsAtom,
  vendorSubscriptionsAtom,
} from '../state';
import { Subscription } from '../types';

const bodyContainerStyle: CSSProperties = {
  position: 'relative',
  height: '170px',
  maxHeight: '220px',
  padding: '10px',
};

const cardBodyStyle: React.CSSProperties = {
  height: '150px',
  maxHeight: '200px',
  position: 'absolute',
  overflow: 'hidden',
};

function SubscriptionsCard(
  id: string,
  subscriptions: Subscription[],
  title: string,
): React.ReactElement {
  const setRefreshSubscription = useSetAtom(refreshSubscriptionsAtom);
  const companyId = useAtomValue(companyIdAtom);
  useEffect(
    () => {
      const scrollBar = new PerfectScrollbar(`#${id}`, {
        suppressScrollX: true,
        suppressScrollY: false,
        wheelPropagation: true,
      });

      return () => {
        scrollBar.destroy();
      };
    },
    [/*at mount*/],
  );

  const handleUnsubscribe = (id) => {
    unsubscribe(id, companyId)
      .then((result) => {
        if (result === true) {
          showSuccessToast('Successfully unsubscribed');
          setRefreshSubscription(Math.random()); // Reset atom only if operation is successful
        } else {
          showFailureToast('Failed to unsubscribe');
        }
      })
      .catch((error) => {
        console.error(error);
        showFailureToast('Failed to unsubscribe');
      });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle tag="div">
          <h4>
            {title} ({subscriptions.length})
          </h4>
        </CardTitle>
      </CardHeader>
      <div style={bodyContainerStyle} className="card-chart rounded">
        <CardBody id={id} style={cardBodyStyle}>
          {subscriptions && subscriptions.length > 0 ? (
            subscriptions.map((subscription) => (
              <BadgeWithRemove
                key={subscription.id}
                onRemove={() => handleUnsubscribe(subscription.id)}
                removeConfirmationTitle="Unsubscribe"
                removeConfirmationMessage="Are you sure you want to unsubscribe the CVE notifications?"
                text={
                  subscription.product_name
                    ? subscription.product_name
                    : subscription.vendor_name
                }
              />
            ))
          ) : (
            <span>
              <NoRecordFound />
            </span>
          )}
        </CardBody>
      </div>
    </Card>
  );
}

const Subscriptions: React.FC = () => {
  const vendorSubscriptions = useAtomValue(vendorSubscriptionsAtom);
  const productSubscriptions = useAtomValue(productSubscriptionsAtom);

  return (
    <Container fluid>
      <Row>
        <Col md={6} sm={12}>
          {SubscriptionsCard(
            'vendors_badges',
            vendorSubscriptions,
            'Vendor Subscriptions',
          )}
        </Col>
        <Col md={6} sm={12}>
          {SubscriptionsCard(
            'products_badges',
            productSubscriptions,
            'Product Subscriptions',
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default Subscriptions;
